import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  CircularProgress,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AuthContext from '../contexts/AuthContext';

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function Agreements() {
  const { tokens } = useContext(AuthContext);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';

  const [agreements, setAgreements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openRows, setOpenRows] = useState(() => new Set());
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [nvOpen, setNvOpen] = useState(false);
  const [nvAgreement, setNvAgreement] = useState(null);
  const [nvVersion, setNvVersion] = useState('');
  const [nvFile, setNvFile] = useState(null);
  const [nvSubmitting, setNvSubmitting] = useState(false);
  const [nvMarkActive, setNvMarkActive] = useState(false);

  const toggleOpen = (id) => {
    setOpenRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!tokens?.id_token) return;
    setLoading(true);
    setError(null);
    const skip = page * rowsPerPage;
    const limit = rowsPerPage;
    const url = `${apiBase}/agreements?skip=${skip}&limit=${limit}`;
    fetch(url, { headers: { Authorization: `Bearer ${tokens.id_token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch agreements');
        return res.json();
      })
      .then(data => {
        setAgreements(Array.isArray(data.items) ? data.items : []);
        setTotal(Number.isFinite(data.total) ? data.total : 0);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [tokens, apiBase, page, rowsPerPage]);

  const versionExists = (agreement, vStr) => {
    if (!agreement || !vStr) return false;
    const all = agreement.versions || [];
    return all.some(v => (v.version || '').trim() === vStr.trim());
  };

  const suggestNextVersion = (agreement) => {
    if (!agreement) return '';
    const vers = (agreement.versions || []).map(v => v.version).filter(Boolean);
    if (vers.length === 0) return '1';
    // Try numeric strategy
    const ints = vers.map(s => (/^\d+$/.test(s) ? parseInt(s, 10) : null)).filter(v => v !== null);
    if (ints.length > 0) {
      const max = Math.max(...ints);
      let candidate = String(max + 1);
      if (!vers.includes(candidate)) return candidate;
      // Rare collision, increment until free
      let i = max + 2;
      while (vers.includes(String(i))) i += 1;
      return String(i);
    }
    // Non-numeric: base on active version if available, else first
    const active = (agreement.versions || []).find(v => v.is_active);
    const base = active?.version || vers[0];
    let n = 1;
    let candidate = `${base}-${n}`;
    while (vers.includes(candidate)) {
      n += 1;
      candidate = `${base}-${n}`;
    }
    return candidate;
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const buildDocumentUrl = (agreementVersionId) => `${apiBase}/agreement-versions/${agreementVersionId}/document`;

  const handleDownload = async (version) => {
    if (!tokens?.id_token) return;
    try {
      const res = await fetch(buildDocumentUrl(version.id), {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      if (!res.ok) throw new Error('Failed to download PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const handleView = async (agreement, version) => {
    if (!tokens?.id_token) return;
    try {
      const res = await fetch(buildDocumentUrl(version.id), {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      if (!res.ok) throw new Error('Failed to load PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewTitle(`${agreement.name} - v${version.version}`);
      setPreviewOpen(true);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      try { window.URL.revokeObjectURL(previewUrl); } catch {}
    }
    setPreviewUrl(null);
  };

  const openNewVersionDialog = (agreement) => {
    setNvAgreement(agreement);
    setNvVersion(suggestNextVersion(agreement));
    setNvFile(null);
    setNvMarkActive(false);
    setNvOpen(true);
  };
  const closeNewVersionDialog = () => {
    if (nvSubmitting) return;
    setNvOpen(false);
    setNvAgreement(null);
    setNvVersion('');
    setNvFile(null);
  };
  const submitNewVersion = async () => {
    if (!tokens?.id_token || !nvAgreement || !nvVersion || !nvFile) return;
    setNvSubmitting(true);
    try {
      const form = new FormData();
      form.append('version', nvVersion);
      form.append('file', nvFile);
      form.append('mark_active', nvMarkActive ? 'true' : 'false');
      const res = await fetch(`${apiBase}/agreements/${nvAgreement.id}/versions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokens.id_token}` },
        body: form,
      });
      if (!res.ok) throw new Error('Failed to upload version');
      // Refresh list
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      const url = `${apiBase}/agreements?skip=${skip}&limit=${limit}`;
      const r2 = await fetch(url, { headers: { Authorization: `Bearer ${tokens.id_token}` } });
      if (r2.ok) {
        const data = await r2.json();
        setAgreements(Array.isArray(data.items) ? data.items : []);
        setTotal(Number.isFinite(data.total) ? data.total : 0);
      }
      closeNewVersionDialog();
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setNvSubmitting(false);
    }
  };

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Agreements
      </Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>Error: {error}</Typography>
      )}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={56}></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Key</TableCell>
                <TableCell align="right">Version</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agreements.map(ag => {
                const open = openRows.has(ag.id);
                const versions = ag.versions || [];
                const activeVersion = versions.find(v => v.is_active);
                return (
                  <React.Fragment key={ag.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'} onClick={() => toggleOpen(ag.id)}>
                          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{ag.name}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {ag.key}
                          <IconButton
                            aria-label="copy key"
                            size="small"
                            onClick={() => navigator.clipboard.writeText(ag.key)}
                            sx={{ ml: 0.5 }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{activeVersion?.version || ''}</TableCell>
                      <TableCell>{formatDate(activeVersion?.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={activeVersion ? 'View active version' : 'No active version'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => activeVersion ? handleView(ag, activeVersion) : null}
                              disabled={!activeVersion}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="New Version">
                          <IconButton size="small" sx={{ ml: 1 }} onClick={() => openNewVersionDialog(ag)}>
                            <UploadFileIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Versions</Typography>
                            {versions.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No versions</Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Binary Asset</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {versions.map(v => (
                                    <TableRow key={v.id} hover>
                                      <TableCell>{v.version}</TableCell>
                                      <TableCell>
                                        {v.is_active ? (
                                          <Chip size="small" color="success" label="Active" />
                                        ) : (
                                          <Chip size="small" label="Inactive" />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center">
                                          {String(v.id).split('-')[0]}
                                          <IconButton
                                            aria-label="copy id"
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(v.id)}
                                            sx={{ ml: 0.5 }}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center">
                                          {String(v.binary_asset_id).split('-')[0]}
                                          <IconButton
                                            aria-label="copy asset id"
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(v.binary_asset_id)}
                                            sx={{ ml: 0.5 }}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                      <TableCell>{formatDate(v.created_at)}</TableCell>
                                      <TableCell align="right">
                                        <Tooltip title="View">
                                          <IconButton size="small" onClick={() => handleView(ag, v)}>
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download">
                                          <IconButton size="small" sx={{ ml: 1 }} onClick={() => handleDownload(v)}>
                                            <OpenInNewIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={v.is_active ? 'Active' : 'Activate'}>
                                          <span>
                                            <IconButton
                                              size="small"
                                              sx={{ ml: 1 }}
                                              disabled={v.is_active}
                                              onClick={async () => {
                                                if (v.is_active) return;
                                                try {
                                                  const res = await fetch(`${apiBase}/agreements/${ag.id}/activate/${v.id}`, {
                                                    method: 'POST',
                                                    headers: { Authorization: `Bearer ${tokens.id_token}` },
                                                  });
                                                  if (!res.ok) throw new Error('Failed to activate version');
                                                  // Refresh list
                                                  const skip = page * rowsPerPage;
                                                  const limit = rowsPerPage;
                                                  const url = `${apiBase}/agreements?skip=${skip}&limit=${limit}`;
                                                  const r2 = await fetch(url, { headers: { Authorization: `Bearer ${tokens.id_token}` } });
                                                  if (r2.ok) {
                                                    const data = await r2.json();
                                                    setAgreements(Array.isArray(data.items) ? data.items : []);
                                                    setTotal(Number.isFinite(data.total) ? data.total : 0);
                                                  }
                                                } catch (err) {
                                                  console.error(err);
                                                  setError(err.message);
                                                }
                                              }}
                                            >
                                              <CheckCircleOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={previewOpen} onClose={closePreview} maxWidth="lg" fullWidth>
        <DialogTitle>{previewTitle || 'View PDF'}</DialogTitle>
        <DialogContent dividers>
          {previewUrl ? (
            <Box sx={{ width: '100%', height: '80vh' }}>
              <embed src={previewUrl} type="application/pdf" width="100%" height="100%" />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nvOpen} onClose={closeNewVersionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Version{nvAgreement ? ` for ${nvAgreement.name}` : ''}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Version</Typography>
              <input
                type="text"
                value={nvVersion}
                onChange={(e) => setNvVersion(e.target.value)}
                placeholder="e.g., 1.2 or 2025-08-27"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
              {nvAgreement && versionExists(nvAgreement, nvVersion) && (
                <Typography variant="caption" color="error">Version already exists for this agreement</Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>PDF File</Typography>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setNvFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
              />
              <Typography variant="caption" color="text.secondary">
                Only PDF files are allowed
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={<Checkbox checked={nvMarkActive} onChange={(e) => setNvMarkActive(e.target.checked)} />}
                label="Mark as Active"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNewVersionDialog} disabled={nvSubmitting}>Cancel</Button>
          <Button onClick={submitNewVersion} disabled={nvSubmitting || !nvVersion || !nvFile || (nvAgreement && versionExists(nvAgreement, nvVersion))} variant="contained">
            {nvSubmitting ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
